import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, LoadingState, ErrorState, ConfirmDialog } from "@/components/common";
import { ViewSwitcher } from "@/components/layout/Record";
import { useGarage } from "@/context/GarageContext";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

const COLS = ["booked", "received", "inspecting", "awaiting_approval", "approved", "in_progress", "ready", "completed", "cancelled"];
const ALL = "__all";

export const since = (iso) => {
  const m = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 60 ? `${m}m` : m < 1440 ? `${Math.floor(m / 60)}h` : `${Math.floor(m / 1440)}d`;
};
const initials = (n) => (n || "").split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0].toUpperCase()).join("");

function JobCard({ j, onOpen, onDragStart, onMove, mobile }) {
  return (
    <div draggable={!mobile} onDragStart={(e) => { e.dataTransfer.setData("text/plain", j.id); onDragStart(j); }} onClick={() => onOpen(j)}
      className="group cursor-pointer rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition-[box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:shadow-md active:cursor-grabbing"
      data-testid={`board-card-${j.number}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs font-semibold text-teal-700">{j.number}</span>
        <span className="text-[11px] text-slate-400" title={t("b12.board.since")} data-testid={`board-card-since-${j.number}`}>{since(j.status_since)}</span>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className="rounded border-2 border-slate-900 bg-yellow-300 px-1.5 font-mono-plate text-xs font-bold text-slate-900" data-testid={`board-card-plate-${j.number}`}>{j.vehicle.plate}</span>
        <span className="truncate text-sm text-slate-700">{j.vehicle.make} {j.vehicle.model}</span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="truncate text-xs text-slate-500">{j.customer.name}</span>
        {j.mechanic_name ? <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#0A2540] text-[10px] font-bold text-white" title={j.mechanic_name}>{initials(j.mechanic_name)}</span>
          : <span className="text-[10px] text-slate-400">{t("jobs.unassigned")}</span>}
      </div>
      {mobile && j.next_statuses.length > 0 && (
        <div className="mt-3" onClick={(e) => e.stopPropagation()}>
          <Select value="" onValueChange={(s) => onMove(j, s)}>
            <SelectTrigger className="h-9 text-xs" data-testid={`board-move-${j.number}`}><SelectValue placeholder={t("b12.board.moveTo")} /></SelectTrigger>
            <SelectContent>{j.next_statuses.map((s) => <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

function Column({ s, jobs, dragging, onDrop, ...cardProps }) {
  const [over, setOver] = useState(false);
  const allowed = dragging && dragging.next_statuses.includes(s);
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(s); }}
      className={`flex w-72 shrink-0 flex-col rounded-xl border bg-slate-100/70 transition-[background-color,border-color,opacity] duration-150 ${over ? "border-teal-500 bg-teal-50" : allowed ? "border-teal-300" : "border-transparent"} ${dragging && !allowed && dragging.status !== s ? "opacity-60" : ""}`}
      data-testid={`board-column-${s}`}>
      <div className="flex items-center justify-between px-3 py-2.5">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-600">{t(`status.${s}`)}</p>
        <span className="rounded-full bg-white px-2 text-xs font-semibold text-slate-600" data-testid={`board-count-${s}`}>{jobs.length}</span>
      </div>
      <div className="flex min-h-24 flex-1 flex-col gap-2 px-2 pb-3">{jobs.map((j) => <JobCard key={j.id} j={j} {...cardProps} />)}</div>
    </div>
  );
}

function useBoard() {
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get("/jobs", { params: { board: true } }).then((r) => { setJobs(r.data); setError(""); }).catch((e) => setError(errMsg(e))), []);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);
  return { jobs, setJobs, error, load };
}

export default function Board() {
  const { access, readOnly } = useGarage();
  const navigate = useNavigate();
  const { jobs, setJobs, error, load } = useBoard();
  const [q, setQ] = useState("");
  const [mech, setMech] = useState(ALL);
  const [showCancelled, setShowCancelled] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [tab, setTab] = useState("received");
  const [confirm, setConfirm] = useState(null);
  const mechanics = useMemo(() => [...new Map((jobs || []).filter((j) => j.mechanic_id).map((j) => [j.mechanic_id, j.mechanic_name])).entries()], [jobs]);
  const visible = (jobs || []).filter((j) => (mech === ALL || j.mechanic_id === mech || (mech === "none" && !j.mechanic_id))
    && (!q || `${j.number} ${j.vehicle.plate} ${j.vehicle.make} ${j.vehicle.model} ${j.customer.name}`.toLowerCase().includes(q.toLowerCase())));
  const cols = COLS.filter((s) => (s !== "cancelled" || showCancelled) && (s !== "booked" || visible.some((j) => j.status === "booked")));
  const move = async (job, to) => {
    if (readOnly || job.status === to) return;
    if (to === "cancelled" && !confirm) return setConfirm({ job, to });
    const before = jobs;
    setJobs(jobs.map((j) => (j.id === job.id ? { ...j, status: to, status_since: new Date().toISOString() } : j)));
    try {
      const { data } = await api.post(`/jobs/${job.id}/status`, { status: to });
      setJobs((cur) => cur.map((j) => (j.id === job.id ? { ...j, status: data.status, next_statuses: data.next_statuses } : j)));
      toast.success(t("jobs.statusChanged", { status: t(`status.${to}`) }));
    } catch (e) {
      setJobs(before);
      toast.error(errMsg(e));
    }
  };
  const drop = (to) => { const j = dragging; setDragging(null); if (j) move(j, to); };
  const cardProps = { onOpen: (j) => navigate(`/pro/jobs/${j.id}`), onDragStart: setDragging, onMove: move };
  return (
    <div data-testid="workshop-board" onDragEnd={() => setDragging(null)}>
      <PageHeader title={t("b12.board.title")} subtitle={access.role === "mechanic" ? t("b12.board.subtitleMechanic") : t("b12.board.subtitle")}
        actions={<Button variant="outline" onClick={load} data-testid="board-refresh"><RefreshCw className="mr-2 h-4 w-4" />{t("b12.board.refresh")}</Button>} />
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <ViewSwitcher current="kanban" views={[["list", "/pro/jobs"], ["kanban"]]} />
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("jobs.search")} className="pl-9" data-testid="board-search" />
        </div>
        {access.role !== "mechanic" && (
          <Select value={mech} onValueChange={setMech}>
            <SelectTrigger className="w-full sm:w-52" data-testid="board-mechanic-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("b12.board.allMechanics")}</SelectItem>
              <SelectItem value="none">{t("jobs.unassigned")}</SelectItem>
              {mechanics.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={showCancelled} onCheckedChange={setShowCancelled} data-testid="board-show-cancelled" />{t("b12.board.showCancelled")}</label>
        <span className="text-xs text-slate-400 sm:ml-auto">{t("b12.board.hint")}</span>
      </div>
      {error && <ErrorState message={error} onRetry={load} />}
      {!jobs && !error && <LoadingState />}
      {jobs && (
        <>
          <div className="hidden gap-3 overflow-x-auto pb-4 md:flex" data-testid="board-columns">
            {cols.map((s) => <Column key={s} s={s} jobs={visible.filter((j) => j.status === s)} dragging={dragging} onDrop={drop} {...cardProps} />)}
          </div>
          <div className="md:hidden" data-testid="board-mobile">
            <div className="-mx-4 mb-3 flex gap-2 overflow-x-auto px-4 pb-1">
              {cols.map((s) => (
                <button key={s} type="button" onClick={() => setTab(s)} data-testid={`board-tab-${s}`}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold ${tab === s ? "border-[#0A2540] bg-[#0A2540] text-white" : "border-slate-200 bg-white text-slate-600"}`}>
                  {t(`status.${s}`)} · {visible.filter((j) => j.status === s).length}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {visible.filter((j) => j.status === tab).map((j) => <JobCard key={j.id} j={j} mobile {...cardProps} />)}
              {!visible.some((j) => j.status === tab) && <p className="py-10 text-center text-sm text-slate-500">{t("b12.board.emptyCol")}</p>}
            </div>
          </div>
        </>
      )}
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} destructive title={t("jobs.cancelTitle")} description={t("jobs.cancelText")} confirmLabel={t("jobs.to.cancelled")}
        onConfirm={() => { const c = confirm; setConfirm(null); move(c.job, c.to); }} testId="board-cancel-dialog" />
    </div>
  );
}
